/**
 * Staff-ui FirmProfile form (create + edit).
 *
 * Aligned with the canonical account-ui `FirmProfileForm`
 * (`services/account-ui/src/pages/Profiles/FirmProfileForm/FirmProfileForm.tsx`)
 * via the shared staff-ui primitives in `@/components/Profiles` (StatusPill,
 * SaveActions, FormValidationSummary, useFormValidation, Repeater). Status is
 * driven by which SaveActions button is pressed — there is no editable status
 * field — and Publish is gated behind `profiles.publish`. Staff-only additions
 * (the CompanyPicker in create mode) are retained because staff author across
 * any firm, unlike the account portal which derives the firm from the session.
 */
import {
  Button,
  ComboField,
  type ComboOption,
  Input,
  Select,
} from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { companies } from "@/api/addressBook.service";
import {
  type ContentStatus,
  extractZodIssues,
  type FirmProfile,
  type FirmProfileCreateInput,
  firmProfileKeys,
  firmProfilesService,
  type FirmProfileUpdateInput,
  type ImageRef,
  type ProfileType,
  type RegionAssignment,
} from "@/api/profiles";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PageHeader } from "@/components/PageHeader";
import {
  FormValidationSummary,
  ImageUploadField,
  Repeater,
  SaveActions,
  StatusPill,
  useFormValidation,
} from "@/components/Profiles";
import { CompanyPicker } from "@/components/Profiles/ListFilters";
import { RegionsPicker } from "@/components/Profiles/RegionPicker";
import { RichTextField } from "@/components/Profiles/RichTextField";
import { LANGUAGE_LABELS, LANGUAGES, type Website, WEBSITE_OPTIONS } from "@/constants/features";
import { SPOKEN_LANGUAGE_OPTIONS } from "@/constants/spokenLanguages";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import styles from "../ProfilesForm.module.css";
import { AuditPanel } from "../Audit/AuditPanel";

const PROFILE_TYPE_OPTIONS: ComboOption[] = [
  { value: "lawfirm", label: "Law firm" },
  { value: "set", label: "Set" },
  { value: "network", label: "Network" },
];

// Options for the Languages repeater. The stored value is the free-text
// language NAME (value === label). Sourced from the shared 50-name vocabulary so
// it stays identical to account-ui.
const LANGUAGE_OPTIONS: ComboOption[] = SPOKEN_LANGUAGE_OPTIONS;

// Set of valid spoken-language names, used by the draft schema to reject rows
// whose value isn't in the controlled vocabulary (e.g. a legacy WP blob like
// "Germany, Scottish & English").
const SPOKEN_LANGUAGE_VALUES = new Set(LANGUAGE_OPTIONS.map(o => o.value));

// Website (publishing target) options derived from the canonical WEBSITE_OPTIONS
// — never re-hardcode the list/order here. Profile-language options are derived
// from the shared LANGUAGES / LANGUAGE_LABELS so wire codes and labels stay in
// a single source of truth.
const WEBSITE_FIELD_OPTIONS = WEBSITE_OPTIONS.map(({ value, label }) => ({ value, label }));
const PROFILE_LANGUAGE_OPTIONS = LANGUAGES.map(code => ({
  value: code,
  label: LANGUAGE_LABELS[code] ?? code,
}));

const RICH_TEXT_MAX = 50_000;

type StaffingEntry = { title: string; value: string };
type MembershipEntry = { membershipText: string };
type LanguageEntry = { languageCode: string };

type StaffFirmProfileFormProps =
  | { mode: "edit"; id: string; initialCompanyId?: never }
  | { mode: "create"; id?: never; initialCompanyId?: string };

type DraftState = {
  companyId: string | null;
  profileType: ProfileType;
  languageCode: string;
  website: Website;
  theFirm: string;
  additionalInformation: string;
  logo: ImageRef | null;
  status: ContentStatus;
  regions: RegionAssignment[];
  staffing: StaffingEntry[];
  memberships: MembershipEntry[];
  languages: LanguageEntry[];
};

function defaultDraft(initialCompanyId?: string): DraftState {
  return {
    companyId: initialCompanyId ?? null,
    profileType: "lawfirm",
    languageCode: "en",
    website: "com_en",
    theFirm: "",
    additionalInformation: "",
    logo: null,
    status: "draft",
    regions: [],
    staffing: [],
    memberships: [],
    languages: [],
  };
}

function fromApi(p: FirmProfile): DraftState {
  return {
    companyId: p.companyId,
    profileType: p.profileType,
    languageCode: p.languageCode ?? "en",
    website: p.website ?? "com_en",
    theFirm: p.theFirm ?? "",
    additionalInformation: p.additionalInformation ?? "",
    logo: p.logoHash && p.logoFilename ? { hash: p.logoHash, filename: p.logoFilename } : null,
    status: p.status,
    // Country-only (§4.2): ignore any legacy `kind:"region"` rows on hydration.
    regions: (p.regions ?? []).filter(r => r.kind === "country"),
    // Detail items carry extra server fields (id, firmProfileId, sortOrder) —
    // map down to the create/update wire shape (mirror account-ui :314-320).
    staffing: p.staffing ? p.staffing.map(({ title, value }) => ({ title, value })) : [],
    memberships: p.memberships
      ? p.memberships.map(({ membershipText }) => ({ membershipText }))
      : [],
    languages: p.languages ? p.languages.map(({ languageCode }) => ({ languageCode })) : [],
  };
}

// Client-side validation (mirror profiles-api InsertFirmProfileSchema, trimmed).
const FirmProfileDraftSchema = z.object({
  theFirm: z.string().min(1, "Firm name / content is required").max(RICH_TEXT_MAX),
  // Repeatable array fields. The user can add blank rows freely, but the
  // server rejects blanks, so we mirror those constraints client-side and
  // force the user to fill or remove a blank row. Staffing `value` is a
  // headcount stored as text but must be a whole number.
  staffing: z
    .array(
      z.object({
        title: z.string().min(1, "Staff type is required"),
        value: z.string().regex(/^\d+$/, "Enter a whole number"),
      }),
    )
    .optional(),
  memberships: z
    .array(z.object({ membershipText: z.string().min(1, "Membership is required") }))
    .optional(),
  languages: z
    .array(
      z.object({
        languageCode: z
          .string()
          .refine(v => SPOKEN_LANGUAGE_VALUES.has(v), "Select a valid language"),
      }),
    )
    .optional(),
});

const FIRM_PROFILE_ARRAY_KEYS = ["staffing", "memberships", "languages"] as const;

const FIRM_PROFILE_ARRAY_FIELD_LABELS = {
  staffing: {
    section: "Staffing",
    rowNoun: "row",
    subFields: { title: "Staff type", value: "Headcount" },
  },
  memberships: {
    section: "Memberships",
    rowNoun: "row",
    subFields: { membershipText: "Membership" },
  },
  languages: { section: "Languages", rowNoun: "row", subFields: { languageCode: "Language" } },
};

type FirmProfileFieldKey = "theFirm" | "languageCode";

const FIRM_PROFILE_FIELD_KEYS = [
  "theFirm",
  "languageCode",
] as const satisfies ReadonlyArray<FirmProfileFieldKey>;

const FIRM_PROFILE_FIELD_LABELS: Record<FirmProfileFieldKey, string> = {
  theFirm: "About the firm",
  languageCode: "Profile language",
};

export function StaffFirmProfileForm(props: StaffFirmProfileFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = props.mode === "edit";
  const { hasPermission } = usePermissions();
  const canPublish = hasPermission("profiles.publish");
  // `additionalInformation` is an admin-only editorial field. Non-admins never
  // see the input and we omit it from the payload entirely so we don't blank
  // existing server data (mirror account-ui :219-223 / :382-384). Gated on its
  // own `profiles.admin` check so widening `canPublish` to `profiles.publish`
  // does not leak this field to all profile editors.
  const isProfilesAdmin = hasPermission("profiles.admin");
  const { hasFlag } = useFeatureFlags();
  const isFirmLogosEditorOn = hasFlag("firm-logos-editor");

  const detailQuery = useQuery({
    queryKey: isEdit ? firmProfileKeys.detail(props.id) : ["__noop_firm_profile_detail__"],
    queryFn: () => firmProfilesService.get(props.id as string),
    enabled: isEdit,
  });

  const [draft, setDraft] = useState<DraftState>(() => defaultDraft(props.initialCompanyId));
  const [formError, setFormError] = useState<string | null>(null);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    errors: fieldErrors,
    arrayErrors,
    summary,
    orphanMessages,
    validate,
    clearError,
    clearArrayError,
    setServerIssues,
  } = useFormValidation({
    schema: FirmProfileDraftSchema,
    labels: FIRM_PROFILE_FIELD_LABELS,
    knownKeys: FIRM_PROFILE_FIELD_KEYS,
    arrayKeys: FIRM_PROFILE_ARRAY_KEYS,
    arrayFieldLabels: FIRM_PROFILE_ARRAY_FIELD_LABELS,
  });

  // Resolve the firm's display name from the address-book company so the Firm
  // field shows a human-readable name instead of the raw company_id UUID.
  const companyId = draft.companyId;
  const companyQuery = useQuery({
    queryKey: ["address-book", "company", companyId],
    queryFn: () => companies.getById(companyId as string),
    enabled: isEdit && Boolean(companyId),
  });

  // Seed draft when detail loads.
  useEffect(() => {
    if (isEdit && detailQuery.data) {
      setDraft(fromApi(detailQuery.data));
    }
  }, [isEdit, detailQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (targetStatus: ContentStatus): Promise<FirmProfile> => {
      const basePayload: FirmProfileCreateInput = {
        profileType: draft.profileType,
        languageCode: draft.languageCode || undefined,
        website: draft.website,
        // Inline firm logo (optional). Send `null` to clear server-side.
        logoHash: draft.logo?.hash ?? null,
        logoFilename: draft.logo?.filename ?? null,
        theFirm: draft.theFirm || undefined,
        // Only admins can edit `additionalInformation`; omit from the payload
        // for non-admins so we don't overwrite server data.
        ...(isProfilesAdmin
          ? { additionalInformation: draft.additionalInformation || undefined }
          : {}),
        status: targetStatus,
        regions: draft.regions,
        staffing: draft.staffing,
        memberships: draft.memberships,
        languages: draft.languages,
      };
      if (isEdit) {
        const patch: FirmProfileUpdateInput = basePayload;
        return firmProfilesService.update(props.id as string, patch);
      }
      if (!draft.companyId) {
        throw new Error("Company is required to create a firm profile");
      }
      const createInput: FirmProfileCreateInput = {
        ...basePayload,
        companyId: draft.companyId,
      };
      return firmProfilesService.create(createInput);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: firmProfileKeys.all });
      navigate(ROUTES.profiles.firmProfiles);
    },
    onError: (err: Error) => {
      const issues = extractZodIssues(err);
      if (issues && issues.length > 0) {
        setServerIssues(issues);
        setFormError(null);
        return;
      }
      setFormError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => firmProfilesService.remove(props.id as string),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: firmProfileKeys.all });
      navigate(ROUTES.profiles.firmProfiles);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  function handleSave(targetStatus: ContentStatus) {
    setFormError(null);
    setRegionsError(null);

    if (!isEdit && !draft.companyId) {
      setFormError("Company is required");
      return;
    }

    const parsed = validate({
      theFirm: draft.theFirm,
      staffing: draft.staffing,
      memberships: draft.memberships,
      languages: draft.languages,
    });
    if (parsed === null) {
      return;
    }

    if (draft.regions.length === 0) {
      setRegionsError("At least one region is required");
      return;
    }

    // Reflect the in-flight transition in the UI (SaveActions drives status).
    setDraft(d => ({ ...d, status: targetStatus }));
    saveMutation.mutate(targetStatus);
  }

  if (isEdit && detailQuery.isPending) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Firm profile" />
        <p role="status" className={styles.muted}>
          Loading…
        </p>
      </div>
    );
  }
  if (isEdit && detailQuery.isError) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Firm profile" />
        <p role="alert" className={styles.errorBanner}>
          {detailQuery.error instanceof Error
            ? detailQuery.error.message
            : "Failed to load firm profile"}
        </p>
      </div>
    );
  }

  const firmName = companyQuery.data?.data?.name ?? companyId ?? "";

  // Derive the Profile Language lock from the canonical WEBSITE_OPTIONS each
  // render so the UI can never drift from the server's allowed-language rule.
  // `allowedLanguages` is null for com_en/com_zh (free choice) and a single-code
  // array for fr/de (locked to that code) — read the locked code from index 0
  // rather than re-encoding the fr→fr / de→de map.
  const selectedWebsite = WEBSITE_OPTIONS.find(o => o.value === draft.website);
  const allowedLanguages = selectedWebsite?.allowedLanguages ?? null;
  const profileLanguageLocked = allowedLanguages !== null;

  function handleWebsiteChange(value: string) {
    const next = value as Website;
    const opt = WEBSITE_OPTIONS.find(o => o.value === next);
    setDraft(d => ({
      ...d,
      website: next,
      // When switching to a locked edition (fr/de), force the profile language
      // to the single allowed code so the saved combo is always server-valid.
      ...(opt?.allowedLanguages ? { languageCode: opt.allowedLanguages[0] } : {}),
    }));
    clearError("languageCode");
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={isEdit ? "Edit firm profile" : "New firm profile"}
        subtitle={
          isEdit && detailQuery.data
            ? `Last updated ${new Date(detailQuery.data.updatedAt).toLocaleString()}`
            : undefined
        }
      >
        <div className={styles.headerActions}>
          <Button variant="outlined" onClick={() => navigate(ROUTES.profiles.firmProfiles)}>
            Cancel
          </Button>
          <SaveActions
            layout="inline"
            currentStatus={draft.status}
            canPublish={canPublish}
            isSaving={saveMutation.isPending}
            onSave={handleSave}
            onDelete={isEdit ? () => setDeleteOpen(true) : undefined}
          />
        </div>
      </PageHeader>

      <FormValidationSummary entries={summary} />

      {(formError || orphanMessages.length > 0) && (
        <div role="alert" className={styles.errorBanner}>
          {formError ?? orphanMessages.join("\n")}
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.main}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Basics</h2>
            <div className={styles.fieldGridSingle}>
              <ComboField
                label="Profile type"
                value={draft.profileType}
                options={PROFILE_TYPE_OPTIONS}
                onChange={value => setDraft(d => ({ ...d, profileType: value as ProfileType }))}
                fullWidth
              />
              <Select
                label="Website"
                value={draft.website}
                options={WEBSITE_FIELD_OPTIONS}
                onChange={e => handleWebsiteChange(e.target.value)}
                fullWidth
              />
              <Select
                label="Profile language"
                value={draft.languageCode}
                options={PROFILE_LANGUAGE_OPTIONS}
                disabled={profileLanguageLocked}
                error={fieldErrors.languageCode}
                onChange={e => {
                  const value = e.target.value;
                  setDraft(d => ({ ...d, languageCode: value }));
                  clearError("languageCode");
                }}
                fullWidth
              />
            </div>
          </section>

          {!isFirmLogosEditorOn && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Branding</h2>
              <ImageUploadField
                label="Logo"
                value={draft.logo}
                onChange={logo => setDraft(d => ({ ...d, logo }))}
                slug={firmName || undefined}
              />
            </section>
          )}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>About the firm</h2>
            <RichTextField
              label="About the firm"
              value={draft.theFirm}
              onChange={(html: string) => {
                setDraft(d => ({ ...d, theFirm: html }));
                clearError("theFirm");
              }}
              preset="basic_enhanced"
              htmlTab
              required
              error={fieldErrors.theFirm}
            />
            {isProfilesAdmin && (
              <RichTextField
                label="Additional information"
                value={draft.additionalInformation}
                onChange={(html: string) => setDraft(d => ({ ...d, additionalInformation: html }))}
                preset="basic"
              />
            )}
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Staffing figures</h2>
            <p className={styles.muted}>
              Headcount by staff type — e.g. Senior Partners: 20, Trainees: 500.
            </p>
            <Repeater
              items={draft.staffing}
              onChange={next => setDraft(d => ({ ...d, staffing: next }))}
              createEmpty={() => ({ title: "", value: "" })}
              addLabel="Add staffing row"
              emptyState="No staffing rows yet."
              rowErrors={arrayErrors.staffing}
              renderRow={(item, index, patch, errs) => (
                <>
                  <Input
                    label="Staff type"
                    fullWidth
                    placeholder="e.g. Senior Partners"
                    error={errs?.title}
                    value={item.title}
                    onChange={e => {
                      patch({ title: e.target.value });
                      clearArrayError("staffing", index, "title");
                    }}
                  />
                  <Input
                    label="Headcount"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    fullWidth
                    error={errs?.value}
                    value={item.value}
                    onChange={e => {
                      patch({ value: e.target.value });
                      clearArrayError("staffing", index, "value");
                    }}
                  />
                </>
              )}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Memberships</h2>
            <Repeater
              items={draft.memberships}
              onChange={next => setDraft(d => ({ ...d, memberships: next }))}
              createEmpty={() => ({ membershipText: "" })}
              addLabel="Add membership"
              emptyState="No memberships yet."
              rowErrors={arrayErrors.memberships}
              renderRow={(item, index, patch, errs) => (
                <Input
                  label="Membership"
                  fullWidth
                  error={errs?.membershipText}
                  value={item.membershipText}
                  onChange={e => {
                    patch({ membershipText: e.target.value });
                    clearArrayError("memberships", index, "membershipText");
                  }}
                />
              )}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Languages</h2>
            <Repeater
              items={draft.languages}
              onChange={next => setDraft(d => ({ ...d, languages: next }))}
              createEmpty={() => ({ languageCode: "English" })}
              addLabel="Add language"
              emptyState="No languages yet."
              rowErrors={arrayErrors.languages}
              renderRow={(item, index, patch, errs) => (
                <ComboField
                  label="Language"
                  fullWidth
                  error={errs?.languageCode}
                  options={LANGUAGE_OPTIONS}
                  value={item.languageCode}
                  onChange={value => {
                    patch({ languageCode: value });
                    clearArrayError("languages", index, "languageCode");
                  }}
                />
              )}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Countries</h2>
            <RegionsPicker
              label="Countries (at least one required)"
              // Scope the picker to the countries the selected Website publishes
              // (spec 19). `selectedWebsite.domain` maps com_en/com_zh →
              // legal500.com, fr → legal500.fr, de → legal500.de.
              site={selectedWebsite?.domain}
              value={draft.regions}
              onChange={regions => {
                setDraft(d => ({ ...d, regions }));
                if (regions.length > 0) setRegionsError(null);
              }}
              error={regionsError}
            />
          </section>
        </div>

        <aside className={styles.sidebar}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Status</h2>
            <StatusPill status={draft.status} />
          </section>

          {!isEdit && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Company</h2>
              <CompanyPicker
                value={draft.companyId}
                onChange={id => setDraft(d => ({ ...d, companyId: id }))}
              />
              <p className={styles.labelHint}>Required: select the firm this profile belongs to.</p>
            </section>
          )}

          {isEdit && companyId && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Company</h2>
              <p className={styles.muted}>{firmName}</p>
            </section>
          )}

          {props.mode === "edit" && <AuditPanel entityType="firm_profile" entityId={props.id} />}
        </aside>
      </div>

      {deleteOpen && isEdit && (
        <ConfirmationModal
          title="Delete firm profile"
          message="Are you sure you want to delete this firm profile?"
          warningMessage="This cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setDeleteOpen(false)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync();
          }}
        />
      )}
    </div>
  );
}
